<template>
  <div class="progress-stepper">
    <div
        v-for="(step, index) in steps"
        :key="index"
        class="step"
        :class="{
        'completed': index < currentStep - 1,
        'current': index === currentStep - 1,
        'upcoming': index > currentStep - 1
      }"
    >
      <div class="step-indicator">
        <div class="step-circle">
          <template v-if="index < currentStep - 1">
            <!-- Checkmark for completed steps -->
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </template>
          <template v-else>
            <!-- Step number for current and upcoming steps -->
            {{ index + 1 }}
          </template>
        </div>
      </div>
      <!-- Progress line between steps -->
      <div v-if="index < steps.length - 1" class="step-line" :class="{
        'completed-line': index < currentStep - 1,
        'current-line': index === currentStep - 1,
        'upcoming-line': index > currentStep - 1
      }"></div>
      <div class="step-label">{{ step.label }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressStepper',
  props: {
    // Current step (1-based index)
    currentStep: {
      type: Number,
      required: true,
      validator: (value) => value > 0
    },
    // Optional custom steps - if not provided, default steps will be used
    customSteps: {
      type: Array,
      default: null
    }
  },
  computed: {
    // Use custom steps if provided, otherwise use default steps
    steps() {
      if (this.customSteps) {
        return this.customSteps;
      }

      // Default steps for document signing process - updated to match image
      return [
        { label: '檢視合約' },
        { label: '簽署合約' },
        { label: '完成確認' }
      ];
    }
  }
}
</script>

<style scoped>
.progress-stepper {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(255, 158, 21, 0.15);
  margin-bottom: 20px;
  position: relative;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
}

.step-indicator {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  justify-content: center;
  margin-bottom: 10px;
}

.step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  z-index: 2;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
}

.step-line {
  height: 3px;
  background-color: #e0e0e0;
  transition: background-color 0.3s ease;
  position: absolute;
  top: 15px;
  left: calc(50% + 15px);
  width: calc(100% - 30px);
  z-index: 1;
}

.step:last-child .step-line {
  display: none;
}

.step-label {
  margin-top: 5px;
  font-size: 14px;
  text-align: center;
  color: #666;
  transition: color 0.3s ease;
  font-weight: 400;
  width: 100%;
}

/* Completed step styling */
.step.completed .step-circle {
  background-color: #FF9E15;
  color: white;
  border: none;
}

.completed-line {
  background-color: #FF9E15 !important;
}

.step.completed .step-label {
  color: #FF9E15;
  font-weight: 500;
}

/* Current step styling */
.step.current .step-circle {
  background-color: white;
  color: #FF9E15;
  border: 2px solid #FF9E15;
}

.step.current .step-label {
  color: #FF9E15;
  font-weight: 600;
}

/* Upcoming step styling */
.step.upcoming .step-circle {
  background-color: white;
  color: #777;
  border: 2px solid #ddd;
}

/* Tablet and Mobile view */
@media (max-width: 768px) {
  .progress-stepper {
    padding: 14px 16px;
  }

  .step-circle {
    width: 28px;
    height: 28px;
  }

  .step-line {
    top: 14px;
    left: calc(50% + 14px);
    width: calc(100% - 28px);
  }

  .step-label {
    font-size: 13px;
  }
}

/* Mobile view */
@media (max-width: 600px) {
  .progress-stepper {
    padding: 12px 14px;
  }

  .step-circle {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .step-line {
    height: 2px;
    top: 13px;
    left: calc(50% + 13px);
    width: calc(100% - 26px);
  }

  .step-label {
    font-size: 12px;
  }
}

/* Small mobile view */
@media (max-width: 400px) {
  .progress-stepper {
    padding: 10px;
  }

  .step-circle {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }

  .step-line {
    top: 12px;
    left: calc(50% + 12px);
    width: calc(100% - 24px);
  }

  .step-label {
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* Extra small screens */
@media (max-width: 320px) {
  .step-circle {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }

  .step-line {
    top: 11px;
    left: calc(50% + 11px);
    width: calc(100% - 22px);
  }

  .step-label {
    font-size: 9px;
  }
}

/* Landscape orientation adjustments */
@media (max-height: 500px) and (orientation: landscape) {
  .progress-stepper {
    padding: 10px 16px;
  }

  .step-indicator {
    height: 30px;
  }

  .step-circle {
    position: relative;
  }

  .step-line {
    top: 15px;
  }

  .step-label {
    margin-top: 5px;
    font-size: 11px;
    min-height: 1.8em;
  }
}
</style>